/**
 * AutopilotHistory — Timeline der bisherigen Autopilot-Aktionen
 *
 * Zeigt eine kompakte Liste aller Autopilot-Erstellungen und Refreshes:
 *   - Monat/Jahr der Aktion
 *   - Plan-Health-Score (Balken + Zahl + verbales Label)
 *   - Score-Verbesserung seit letzter Aktion (wenn vorhanden)
 *   - Kurze Zusammenfassung was sich geändert hat (aufklappbar)
 *   - Zeitstempel
 *
 * Design-Entscheidungen:
 * - Komplett unsichtbar wenn keine Daten (kein leerer Container)
 * - Lädt im Hintergrund, blockiert nichts
 * - Fokus auf Story: "Wird mein Plan besser?"
 * - Keine rohen IDs, keine technischen Felder
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { History, ChevronDown, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Hilfsfunktionen ───────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "", "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

const MONTH_FULL = [
  "", "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const formatDate = (isoStr) => {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const SOURCE_LABELS = {
  autopilot_create:  "Plan erstellt",
  autopilot_refresh: "Plan aktualisiert",
};

// ── Farbkodierung Health-Score ────────────────────────────────────────────────

const scoreColor = (score) => {
  if (score >= 70) return "text-green-600";
  if (score >= 50) return "text-amber-500";
  return "text-red-500";
};

const barColor = (score) => {
  if (score >= 70) return "bg-green-400";
  if (score >= 50) return "bg-amber-400";
  return "bg-red-400";
};

// ── Einzelner Historien-Eintrag ───────────────────────────────────────────────

const HistoryEntry = ({ item }) => {
  const [open, setOpen] = useState(false);
  const {
    month, year, health_score = 0, health_label = "",
    score_delta = 0, change_summary = [], created_at, source, used_analytics,
  } = item;

  const sourceLabel = SOURCE_LABELS[source] || "Autopilot-Aktion";

  return (
    <div className="px-5 py-4 space-y-2">
      {/* Hauptzeile */}
      <div className="flex items-start gap-3">
        {/* Monat-Badge */}
        <div className="flex-shrink-0 w-12 pt-0.5 text-center">
          <p className="text-xs font-bold text-stone-700 leading-tight">
            {MONTH_FULL[month] || `Monat ${month}`}
          </p>
          <p className="text-xs text-stone-400">{year}</p>
        </div>

        {/* Health-Bar + Score */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2.5 bg-stone-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barColor(health_score)}`}
                style={{ width: `${Math.min(100, health_score)}%` }}
              />
            </div>
            <span className={`text-sm font-bold tabular-nums flex-shrink-0 w-7 text-right ${scoreColor(health_score)}`}>
              {health_score}
            </span>
          </div>

          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-stone-500 flex-1 truncate">{health_label}</span>
            {score_delta > 0 && (
              <span className="text-xs font-semibold text-green-500 flex-shrink-0">
                +{score_delta} ↑
              </span>
            )}
            {score_delta < 0 && (
              <span className="text-xs text-stone-400 flex-shrink-0">
                {score_delta}
              </span>
            )}
          </div>
        </div>

        {/* Rechte Spalte: Datum + Aktion */}
        <div className="flex-shrink-0 text-right">
          <p className="text-xs text-stone-400">{formatDate(created_at)}</p>
          <p className="text-xs text-stone-500 mt-0.5">{sourceLabel}</p>
          {used_analytics && (
            <span className="inline-flex items-center gap-0.5 text-xs text-blue-500 mt-0.5">
              <Sparkles size={9} />
              datenbasiert
            </span>
          )}
        </div>
      </div>

      {/* Aufklappbare Change-Summary */}
      {change_summary.length > 0 && change_summary[0] !== "Plan neu mit Autopilot erstellt" && (
        <div className="ml-15 pl-15">
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600 transition-colors ml-[60px]"
          >
            <ChevronDown
              size={11}
              className={`transition-transform ${open ? "rotate-180" : ""}`}
            />
            {open ? "Weniger anzeigen" : "Was hat sich geändert?"}
          </button>
          {open && (
            <ul className="ml-[60px] mt-1.5 space-y-1">
              {change_summary.map((item, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-stone-500">
                  <span className="text-green-400 flex-shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────

const AutopilotHistory = ({ limit = 6 }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/autopilot/history?limit=${limit}`)
      .then((res) => setData(res.data))
      .catch(() => { /* Silently ignore — non-critical */ })
      .finally(() => setLoading(false));
  }, [limit]);

  // Komplett unsichtbar wenn keine Daten
  if (loading || !data?.has_data || !data.items?.length) return null;

  // Bestes Beispiel: wie viel hat sich Health-Score verbessert?
  const refreshItems = data.items.filter((i) => i.source === "autopilot_refresh");
  const totalImprovement = refreshItems.reduce((sum, i) => sum + Math.max(0, i.score_delta || 0), 0);

  return (
    <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
      {/* Header — immer sichtbar, klappt Body auf/zu */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-stone-50 border-b border-stone-100 hover:bg-stone-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <History size={15} className="text-stone-400" />
          <p className="text-sm font-semibold text-stone-700">Autopilot-Historie</p>
          <span className="text-xs text-stone-400">
            · {data.items.length} {data.items.length === 1 ? "Eintrag" : "Einträge"}
          </span>
          {totalImprovement > 0 && (
            <span className="text-xs text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full ml-1">
              Gesamt +{totalImprovement} Punkte
            </span>
          )}
        </div>
        <ChevronDown
          size={15}
          className={`text-stone-400 transition-transform ${collapsed ? "" : "rotate-180"}`}
        />
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="divide-y divide-stone-100">
          {data.items.map((item, i) => (
            <HistoryEntry key={`${item.month}-${item.year}-${item.created_at}-${i}`} item={item} />
          ))}

          {data.total === limit && (
            <p className="px-5 py-3 text-xs text-stone-400 text-center">
              Die letzten {limit} Einträge werden angezeigt.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AutopilotHistory;
